import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

import { API_PM_MEMBERS } from "../../../config/Endpoints.js";
import type {
  AllocationPeriodUi,
  EmployeeAllocationRow,
  ProjectWithAllocations,
} from "../../types/time-allocation.ts";
import { getApi } from "../../utils/api.ts";

export type PmMemberApi = {
  employeeId: number;
  fullName: string;
  designation: string;
  allottedMinutes: number;
  consumedMinutes: number;
  remainingMinutes: number;
  availableMinutes?: number;
  alreadyAllocated?: number;
  allocationType: string;
  periodType: string | null;
  startDate?: string | null;
  endDate?: string | null;
  month?: number | null;
  year?: number | null;
  createdAt?: string;
};

export type PmProjectApi = {
  projectId: number;
  projectName: string;
  members: PmMemberApi[];
};

function mapPeriodToUi(periodType?: string | null): AllocationPeriodUi {
  if (periodType == null || periodType === "") return "MONTHLY";
  const u = periodType.toUpperCase();
  if (u === "WEEKLY") return "WEEKLY";
  if (u === "MONTHLY") return "MONTHLY";
  return "CUSTOM";
}

/** Hours from API `availableMinutes` only (no client-side fallback). */
function memberAvailableHours(m: PmMemberApi): number {
  return (m.availableMinutes ?? 0) / 60;
}

export function mapPmProjectsToAllocations(
  data: PmProjectApi[],
): ProjectWithAllocations[] {
  if (!Array.isArray(data)) return [];
  return data.map((p) => {
    const first = p.members?.[0];
    const period = mapPeriodToUi(first?.periodType);
    const employees: EmployeeAllocationRow[] = (p.members ?? []).map((m) => ({
      id: `row-${p.projectId}-${m.employeeId}`,
      employeeId: String(m.employeeId),
      name: m.fullName ?? "",
      designation: m.designation?.trim() ?? "",
      allocationType: m.allocationType?.trim() || "GLOBAL",
      allottedMinutes: m.allottedMinutes ?? 0,
      alreadyAllocated: m.alreadyAllocated ?? 0,
      availableHours: memberAvailableHours(m),
      apiRemainingHours: (m.remainingMinutes ?? 0) / 60,
    }));
    return {
      id: String(p.projectId),
      name: p.projectName ?? "",
      period,
      allocationDate: first?.startDate ?? first?.createdAt,
      customStartDate: first?.startDate ?? undefined,
      customEndDate: first?.endDate ?? undefined,
      allocationMonth: first?.month ?? null,
      allocationYear: first?.year ?? null,
      employees,
    };
  });
}

export function usePmMembers() {
  const [projects, setProjects] = useState<ProjectWithAllocations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = Cookies.get("accessToken");
      const { data } = await getApi<PmProjectApi[]>(API_PM_MEMBERS, token);
      setProjects(mapPmProjectsToAllocations(Array.isArray(data) ? data : []));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load projects.");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  return { projects, loading, error, refetch: fetchMembers };
}
